<script setup lang="ts">
  import { computed, watch } from 'vue'
  import { testIds } from '@gat-ship/types'
  import {
    DataList,
    DataListItem,
    DataListItemTitle,
    FlagIcon,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription,
    useModalStack
  } from '@gat-ship/vue-components'
  import { IconLoading, IconPort } from '@gat-ship/icons'
  import { useSelectAgencyPortsDataSource } from '@gat-ship/ref-data'

  /**
   * The sea ports an agency operates in, backed by the live
   * `GET /ref-data/organization/agencies/ports` endpoint — the app's single
   * ports surface (the administration ports panel was retired). The agency is
   * driven by the modal sidebar's agency selector via the `id` prop; the
   * ref-data endpoint exposes no mutations, so the list is view-only
   * (no add/remove/default), and each port opens the central-host port
   * detail modal.
   */
  const props = defineProps<{ id: string }>()

  const { ports, loadingPorts, fetchPorts } = useSelectAgencyPortsDataSource()

  const stack = useModalStack()

  /**
   * Open the central-host port detail modal for the clicked port. The modal
   * fetches `GET /ref-data/geography/ports/local/{locationCode}`, which is
   * keyed by the same `locationCode` this served-ports endpoint returns, so
   * the value passes through untransformed.
   */
  function openPortDetail(locationCode: string) {
    stack.open('port', { locationCode })
  }

  watch(
    () => props.id,
    (agencyId) => {
      fetchPorts(agencyId ?? '')
    },
    { immediate: true }
  )

  const portsList = computed(() => ports.value ?? [])
</script>

<template>
  <ModalSectionHeader>
    <ModalSectionHeaderTitle>Agency Ports</ModalSectionHeaderTitle>
    <ModalSectionHeaderDescription>
      The sea ports this agency operates in.
    </ModalSectionHeaderDescription>
  </ModalSectionHeader>

  <div
    v-if="loadingPorts"
    class="text-muted-foreground mx-auto flex animate-pulse items-center justify-center gap-2 p-6"
  >
    <IconLoading class="size-6" /> Loading ports…
  </div>

  <div
    v-else-if="portsList.length === 0"
    class="text-muted-foreground mx-auto flex flex-col items-center gap-2 p-10 text-center"
  >
    <IconPort class="size-8 opacity-60" />
    <div class="text-sm">No ports connected to this agency.</div>
  </div>

  <DataList v-else>
    <DataListItem
      v-for="port in portsList"
      :key="port.locationCode"
      :data-testid="testIds.port.list.portRow(port.locationCode)"
    >
      <div class="flex w-full items-center gap-3">
        <FlagIcon
          :flag="port.locationCode.slice(0, 2)"
          class="size-5 shrink-0 rounded-sm"
        />
        <DataListItemTitle>
          <div class="flex items-baseline gap-2 text-sm">
            <button
              type="button"
              class="group flex items-baseline gap-2 text-left"
              @click="openPortDetail(port.locationCode)"
            >
              <span class="font-medium group-hover:underline">
                {{ port.name }}
              </span>
              <span class="text-muted-foreground font-normal">
                {{ port.locationCode }}
              </span>
            </button>
            <span
              v-if="port.isDefault"
              class="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-xs"
            >
              Default
            </span>
          </div>
        </DataListItemTitle>
      </div>
    </DataListItem>
  </DataList>
</template>
